
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { useQuery } from "@tanstack/react-query";
import { 
  ArrowLeft, 
  Calendar, 
  CreditCard, 
  FileText, 
  User, 
  Home, 
  Briefcase,
  CheckCircle2,
  XCircle,
  AlertCircle
} from "lucide-react";

// Application status badge
const ApplicationStatusBadge = ({ status }) => {
  const getStatusColor = () => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800 hover:bg-yellow-100";
      case "approved":
        return "bg-green-100 text-green-800 hover:bg-green-100";
      case "declined":
        return "bg-red-100 text-red-800 hover:bg-red-100";
      case "review":
        return "bg-blue-100 text-blue-800 hover:bg-blue-100";
      default:
        return "bg-gray-100 text-gray-800 hover:bg-gray-100";
    }
  };

  return (
    <Badge className={getStatusColor()} variant="outline">
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
};

// Mock data for a single application
const getMockApplication = (id) => {
  return {
    id,
    borrower_name: "Alice Johnson",
    amount: 15000,
    credit_score: "720",
    application_date: "2023-10-25",
    status: "pending",
    loan_purpose: "Home renovation",
    loan_term: "36 months",
    interest_rate: "4.5%",
    monthly_income: "$5,200",
    debt_to_income: "32%",
    employment_status: "Full-time",
    employer: "Tech Solutions Inc.",
    address: "123 Main St, Anytown, CA 12345",
    phone: "(555) 123-4567",
    email: "alice@example.com"
  };
};

const ApplicationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("overview");

  const { data: application, isLoading } = useQuery({
    queryKey: ['application-details', id],
    queryFn: async () => {
      try {
        // In a real implementation, this would fetch data from Supabase
        // For demo purposes, we'll return mock data
        return getMockApplication(id);
      } catch (error) {
        console.error('Error fetching application details:', error);
        toast({
          title: "Error",
          description: "Failed to load application details",
          variant: "destructive",
        });
        return null;
      }
    }
  });

  if (isLoading || !application) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          className="flex items-center gap-2"
          onClick={() => navigate('/lender/loans/applications')}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Applications
        </Button>
        <ApplicationStatusBadge status={application.status} />
      </div>

      <div className="flex flex-col md:flex-row justify-between gap-4">
        <h1 className="text-3xl font-bold">Application #{application.id}</h1>
        <div className="flex gap-2">
          {application.status === "pending" || application.status === "review" ? (
            <>
              <Button 
                variant="outline" 
                className="gap-2"
                onClick={() => navigate(`/lender/loans/applications/${id}/review`)}
              >
                <FileText className="h-4 w-4" />
                Review Application
              </Button>
            </>
          ) : null}
        </div>
      </div>

      <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="borrower">Borrower</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle>Loan Details</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Amount Requested</p>
                  <p className="text-lg font-semibold">${application.amount.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Loan Purpose</p>
                  <p className="text-lg">{application.loan_purpose}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date Applied</p>
                  <p className="text-lg">{application.application_date}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Loan Term</p>
                  <p className="text-lg">{application.loan_term}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Interest Rate</p>
                  <p className="text-lg">{application.interest_rate}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Credit Score</p>
                  <p className="text-lg">{application.credit_score}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Financial Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Monthly Income</p>
                  <p className="text-lg">{application.monthly_income}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Debt-to-Income Ratio</p>
                  <p className="text-lg">{application.debt_to_income}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Employment Status</p>
                  <p className="text-lg">{application.employment_status}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Employer</p>
                  <p className="text-lg">{application.employer}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="borrower" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle>Borrower Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Full Name</p>
                  <p className="text-lg font-semibold">{application.borrower_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="text-lg">{application.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="text-lg">{application.phone}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="text-lg">{application.address}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Credit Score</p>
                  <p className="text-lg">{application.credit_score}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle>Submitted Documents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 border rounded-md flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="font-medium">ID Verification</p>
                      <p className="text-sm text-muted-foreground">Uploaded on {application.application_date}</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">View</Button>
                </div>
                <div className="p-4 border rounded-md flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <Briefcase className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="font-medium">Proof of Income</p>
                      <p className="text-sm text-muted-foreground">Uploaded on {application.application_date}</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">View</Button>
                </div>
                <div className="p-4 border rounded-md flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="font-medium">Bank Statements</p>
                      <p className="text-sm text-muted-foreground">Uploaded on {application.application_date}</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">View</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ApplicationDetails;
